import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { User, MapPin, Heart, Camera, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ETHNICITIES = [
  { value: 'white', label: 'White/Caucasian' },
  { value: 'black', label: 'Black/African American' },
  { value: 'asian', label: 'Asian' },
  { value: 'hispanic', label: 'Hispanic/Latino' },
  { value: 'middle_eastern', label: 'Middle Eastern' },
  { value: 'native_american', label: 'Native American' },
  { value: 'pacific_islander', label: 'Pacific Islander' },
  { value: 'mixed', label: 'Mixed/Multiracial' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

const RELATIONSHIP_GOALS = [
  { value: 'casual', label: 'Casual Dating' },
  { value: 'dating', label: 'Dating & Seeing Where It Goes' },
  { value: 'serious', label: 'Serious Relationship' },
  { value: 'marriage', label: 'Marriage-Minded' }
];

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 
  'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 
  'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 
  'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 
  'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 
  'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 
  'Wisconsin', 'Wyoming'
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [loading, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    ethnicity: user?.ethnicity || '',
    location_city: user?.location_city || '',
    location_state: user?.location_state || '',
    bio: user?.bio || '',
    looking_for: user?.looking_for || '',
    relationship_goal: user?.relationship_goal || '',
    profile_photo_url: user?.profile_photo_url || ''
  });

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/profile`, formData);
      updateUser(response.data);
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const quizCount = user?.quiz_scores ? Object.keys(user.quiz_scores).length : 0;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto" data-testid="profile-page">
        <div className="mb-8">
          <h1 className="text-3xl font-serif font-semibold mb-2">Your Profile</h1>
          <p className="text-muted-foreground">Manage your profile and preferences</p>
        </div>

        {/* Profile Header Card */}
        <Card className="rounded-2xl border-border/50 shadow-soft mb-6 overflow-hidden">
          <div className="h-24 gradient-primary" />
          <CardContent className="relative pt-0">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
              <div className="relative">
                <Avatar className="w-24 h-24 border-4 border-background">
                  <AvatarImage src={formData.profile_photo_url} />
                  <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                    {user?.display_name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="pb-2">
                <h2 className="text-xl font-medium">{user?.display_name}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="secondary" className="rounded-full">
                <User className="w-3 h-3 mr-1" />
                {user?.gender}
              </Badge>
              {user?.location_city && (
                <Badge variant="secondary" className="rounded-full">
                  <MapPin className="w-3 h-3 mr-1" />
                  {user.location_city}, {user.location_state}
                </Badge>
              )}
              <Badge variant="secondary" className="rounded-full">
                <Heart className="w-3 h-3 mr-1" />
                {quizCount} quizzes completed
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Edit Form */}
        <Card className="rounded-2xl border-border/50 shadow-soft">
          <CardHeader>
            <CardTitle className="text-xl font-serif">Edit Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Photo URL */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Profile Photo URL
              </Label>
              <Input
                placeholder="Paste a photo URL"
                value={formData.profile_photo_url}
                onChange={(e) => handleChange('profile_photo_url', e.target.value)}
                className="rounded-xl"
                data-testid="profile-photo-input"
              />
            </div>

            {/* Ethnicity */}
            <div className="space-y-2">
              <Label>Ethnicity</Label>
              <Select value={formData.ethnicity} onValueChange={(v) => handleChange('ethnicity', v)}>
                <SelectTrigger className="rounded-xl" data-testid="ethnicity-select">
                  <SelectValue placeholder="Select your ethnicity" />
                </SelectTrigger>
                <SelectContent>
                  {ETHNICITIES.map((e) => (
                    <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  placeholder="Your city"
                  value={formData.location_city}
                  onChange={(e) => handleChange('location_city', e.target.value)}
                  className="rounded-xl"
                  data-testid="city-input"
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Select value={formData.location_state} onValueChange={(v) => handleChange('location_state', v)}>
                  <SelectTrigger className="rounded-xl" data-testid="state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Looking For */}
            <div className="space-y-2">
              <Label>Looking for</Label>
              <Select value={formData.looking_for} onValueChange={(v) => handleChange('looking_for', v)}>
                <SelectTrigger className="rounded-xl" data-testid="looking-for-select">
                  <SelectValue placeholder="Who are you interested in?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Men</SelectItem>
                  <SelectItem value="female">Women</SelectItem>
                  <SelectItem value="non_binary">Non-binary</SelectItem>
                  <SelectItem value="other">Everyone</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Relationship Goal */}
            <div className="space-y-2">
              <Label>Relationship Goal</Label>
              <Select value={formData.relationship_goal} onValueChange={(v) => handleChange('relationship_goal', v)}>
                <SelectTrigger className="rounded-xl" data-testid="relationship-goal-select">
                  <SelectValue placeholder="What are you looking for?" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_GOALS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Bio */}
            <div className="space-y-2">
              <Label>Bio</Label>
              <Textarea
                placeholder="Tell others about yourself..."
                value={formData.bio}
                onChange={(e) => handleChange('bio', e.target.value)}
                className="rounded-xl min-h-[120px]"
                maxLength={500}
                data-testid="bio-textarea"
              />
              <p className="text-xs text-muted-foreground text-right">
                {formData.bio.length}/500 characters
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <Button 
                onClick={handleSave}
                disabled={loading}
                className="rounded-full flex-1"
                data-testid="save-profile-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="rounded-full text-destructive hover:bg-destructive/10"
                data-testid="logout-btn"
              >
                Log Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default ProfilePage;
